import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Users, Repeat, TrendingUp, IndianRupee, Wallet, Receipt, BarChart3, CalendarDays, LayoutGrid, Trophy, Target,
  Gauge, PiggyBank, Percent, Flame, UserPlus, CalendarPlus,
} from 'lucide-react';
import { getMyGym } from '../api/gymApi';
import { getMyGymAnalytics } from '../api/analyticsApi';
import StatCard from '../components/StatCard';
import LineChart from '../components/LineChart';
import DonutChart from '../components/DonutChart';
import BarChartComp from '../components/BarChart';
import StackedBarChart from '../components/StackedBarChart';
import GaugeChart from '../components/Gauge';
import { occupancyColor, occupancySolid } from '../lib/heatColor';
import { todayLocalDateStr } from '../utils/helpers';

const RANGES = [
  { id: '30', label: '30 Days', days: 30 },
  { id: '90', label: '90 Days', days: 90 },
  { id: 'all', label: 'All Time', days: null },
];

const SECTIONS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'revenue', label: 'Revenue', icon: Wallet },
  { id: 'occupancy', label: 'Occupancy', icon: LayoutGrid },
  { id: 'customers', label: 'Customers', icon: Users },
];

const STATUS_SERIES = [
  { key: 'Completed', label: 'Completed', color: '#34A853' },
  { key: 'Cancelled', label: 'Cancelled', color: '#EA4335' },
  { key: 'Upcoming', label: 'Upcoming', color: '#1a73e8' },
];

const CUSTOMER_SEGMENT_SERIES = [
  { key: 'New', label: 'New', color: '#34A853' },
  { key: 'Returning', label: 'Returning', color: '#9334e6' },
];

const PLAN_LABELS = { daily: 'Daily Pass', weekly: 'Weekly Pass', monthly: 'Monthly Pass' };
const PLAN_COLORS = { 'Daily Pass': '#1a73e8', 'Weekly Pass': '#34A853', 'Monthly Pass': '#9334e6' };

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TIME_BAND_META = {
  morning: { sub: '6–10am' }, midday: { sub: '10am–2pm' }, afternoon: { sub: '2–6pm' }, evening: { sub: '6–10pm' },
};
const BAND_ORDER = ['morning', 'midday', 'afternoon', 'evening'];

const WEEKDAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const EMPTY_ANALYTICS = {
  range: 'all', uniqueUsers: 0, retainedUsers: 0, retentionRate: 0, newBookings: 0, returningBookings: 0,
  revenue: 0, activeBookings: 0, trend: [], statusSeries: [], revenueSeries: [], planMix: [], occupancy: [],
  revenueGoal: 0, totalCustomers: 0, repeatCustomers: 0, avgBookingsPerCustomer: 0, avgSpendPerCustomer: 0,
  topCustomers: [], customerSeries: [],
};

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const [gym, setGym] = useState(null);
  const [gymLoading, setGymLoading] = useState(true);
  const [range, setRange] = useState('all');
  const [section, setSection] = useState('overview');
  const [analytics, setAnalytics] = useState(EMPTY_ANALYTICS);
  const [loading, setLoading] = useState(true);
  const [visibleStatuses, setVisibleStatuses] = useState({ Completed: true, Cancelled: true, Upcoming: true });

  const toggleStatus = (key) => setVisibleStatuses((v) => ({ ...v, [key]: !v[key] }));

  useEffect(() => {
    getMyGym().then(setGym).catch(() => setGym(null)).finally(() => setGymLoading(false));
  }, []);

  useEffect(() => {
    if (gymLoading || !gym) { setLoading(false); return; }
    setLoading(true);
    getMyGymAnalytics({ range })
      .then((data) => setAnalytics({ ...EMPTY_ANALYTICS, ...(data || {}) }))
      .catch(() => setAnalytics(EMPTY_ANALYTICS))
      .finally(() => setLoading(false));
  }, [gym, gymLoading, range]);

  if (gymLoading || loading) {
    return (
      <div className="flex items-center justify-center py-40">
        <div className="w-10 h-10 rounded-full border-4 border-[#e8f0fe] dark:border-[#1a2e4c] border-t-[#1a73e8] animate-spin" />
      </div>
    );
  }

  if (!gym) {
    return (
      <div className="max-w-md mx-auto text-center py-16">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-[#e8f0fe] dark:bg-[#1a2e4c] text-[#1a73e8]">
          <CalendarPlus className="w-7 h-7" />
        </div>
        <h3 className="font-semibold text-base text-[#202124] dark:text-[#e8eaed]">No gym listing yet</h3>
        <p className="text-sm text-[#5f6368] dark:text-[#9aa0a6] mt-1">Create your gym first to start collecting analytics.</p>
        <Link to="/my-gym" className="inline-block mt-4 text-sm font-semibold text-white px-5 py-2.5 rounded-full bg-[#1a73e8] hover:bg-[#1557b0]">
          Create Gym Listing
        </Link>
      </div>
    );
  }

  const rangeDays = RANGES.find((r) => r.id === range)?.days;
  const today = new Date();
  const todayStr = todayLocalDateStr();
  const thisMonthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

  const bookingsTrend = analytics.trend.map((t) => ({ label: t.label, value: t.count }));
  const realizedRevenue = analytics.revenueSeries.reduce((a, r) => a + r.revenue, 0);
  const revenueTrend = analytics.revenueSeries.map((r) => ({ label: r.label, value: r.revenue }));

  const completedCount = analytics.statusSeries.reduce((a, b) => a + (b.values.Completed || 0), 0);
  const avgBookingValue = completedCount > 0 ? Math.round(realizedRevenue / completedCount) : 0;

  const revenueTodayEntry = range !== 'all' ? analytics.revenueSeries.find((r) => r.label === todayStr) : null;

  const daysForAvg = rangeDays ? Math.min(rangeDays, 30) : 30;
  const projectedMonthly = Math.round((realizedRevenue / daysForAvg) * 30);

  const thisMonthRevenue = range === 'all'
    ? analytics.revenueSeries.find((r) => r.label === thisMonthKey)?.revenue || 0
    : analytics.revenueSeries.filter((r) => r.label.slice(0, 7) === thisMonthKey).reduce((a, r) => a + r.revenue, 0);
  const goalPct = analytics.revenueGoal > 0 ? Math.min(100, Math.round((thisMonthRevenue / analytics.revenueGoal) * 100)) : 0;

  const planMixSegments = analytics.planMix
    .filter((p) => p.amount > 0)
    .map((p) => ({ value: p.amount, color: PLAN_COLORS[PLAN_LABELS[p.passType]] || '#9aa0a6', label: PLAN_LABELS[p.passType] || p.passType }));

  const activeStatusSeries = STATUS_SERIES.filter((s) => visibleStatuses[s.key]);
  const statusScaleMax = Math.max(...analytics.statusSeries.map((d) => STATUS_SERIES.reduce((a, s) => a + (d.values[s.key] || 0), 0)), 1) * 1.15;

  // Bookings by day of week, summed across every time band already
  // computed server-side in `occupancy`.
  const byDayOfWeek = DAY_LABELS.map((label, dow) => ({
    label,
    value: analytics.occupancy.filter((o) => o.dayOfWeek === dow).reduce((a, o) => a + o.booked, 0),
  }));

  // Weekly occupancy heatmap grid: occupancy is already day-of-week × band.
  const heatmapByDay = DAY_LABELS.map((label, dow) => ({
    label,
    bands: BAND_ORDER.map((bandKey) => analytics.occupancy.find((o) => o.dayOfWeek === dow && o.band === bandKey) || { pct: null, capacity: 0, booked: 0 }),
  }));
  const bandAverages = BAND_ORDER.map((bandKey) => {
    const cells = analytics.occupancy.filter((o) => o.band === bandKey && o.pct !== null);
    const avgPct = cells.length ? cells.reduce((a, c) => a + c.pct, 0) / cells.length : -1;
    return { key: bandKey, label: bandKey.charAt(0).toUpperCase() + bandKey.slice(1), sub: TIME_BAND_META[bandKey].sub, pctAvg: avgPct, value: avgPct >= 0 ? Math.round(avgPct) : 0 };
  });
  const allPcts = analytics.occupancy.map((o) => o.pct).filter((p) => p !== null);
  const avgWeeklyOccupancy = allPcts.length ? Math.round(allPcts.reduce((a, p) => a + p, 0) / allPcts.length) : 0;
  const timeBandsTracked = analytics.occupancy.filter((o) => o.capacity > 0).length;
  const busiestBand = bandAverages.reduce((best, b) => (b.pctAvg > best.pctAvg ? b : best), { label: '—', pctAvg: -1 });
  const closedDaysCount = WEEKDAY_KEYS.filter((wk) => !(gym.weeklySlots?.[wk] || []).some((s) => s.isActive !== false)).length;

  const customerScaleMax = Math.max(...analytics.customerSeries.map((d) => CUSTOMER_SEGMENT_SERIES.reduce((a, s) => a + (d.values[s.key] || 0), 0)), 1) * 1.15;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-[#202124] dark:text-[#e8eaed]">Analytics</h2>
        <div className="inline-flex p-1 bg-[#f1f3f4] dark:bg-[#202124] rounded-full self-start">
          {RANGES.map((r) => (
            <button key={r.id} onClick={() => setRange(r.id)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                range === r.id ? 'bg-white dark:bg-[#303134] text-[#1a73e8] shadow-sm' : 'text-[#5f6368] dark:text-[#9aa0a6]'
              }`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-[#e8eaed] dark:border-[#3c4043] pb-2 overflow-x-auto">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <button key={s.id} onClick={() => setSection(s.id)}
              className={`px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer flex items-center gap-1.5 whitespace-nowrap ${
                section === s.id ? 'bg-[#e8f0fe] dark:bg-[#1a2e4c] text-[#1a73e8] dark:text-[#8ab4f8] font-semibold' : 'text-[#5f6368] dark:text-[#9aa0a6] hover:bg-[#f1f3f4] dark:hover:bg-[#303134]'
              }`}>
              <Icon className="w-4 h-4" />
              {s.label}
            </button>
          );
        })}
      </div>

      {section === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Users} label="Unique Users" value={analytics.uniqueUsers} tone="blue" />
            <StatCard icon={Repeat} label="Retained" value={analytics.retainedUsers} tone="purple" />
            <StatCard icon={TrendingUp} label="Retention Rate" value={`${analytics.retentionRate}%`} tone="green" />
            <StatCard icon={IndianRupee} label="Revenue" value={`₹${realizedRevenue.toLocaleString('en-IN')}`} tone="amber" sparkline={analytics.revenueSeries.map((r) => r.revenue)} />
          </div>

          <div className="google-card p-6 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
            <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-6">Bookings Trend</h3>
            {bookingsTrend.length === 0 ? <p className="text-xs text-[#9aa0a6] text-center py-10">No bookings in this period yet.</p> : <LineChart data={bookingsTrend} color="#1a73e8" />}
          </div>

          <div className="google-card p-6 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] flex items-center gap-1.5">
                <BarChart3 className="w-4 h-4 text-[#1a73e8]" /> Bookings by Status
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                {STATUS_SERIES.map((s) => {
                  const active = visibleStatuses[s.key];
                  return (
                    <button key={s.key} onClick={() => toggleStatus(s.key)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border cursor-pointer transition-colors ${active ? 'border-transparent' : 'border-[#dadce0] dark:border-[#3c4043] opacity-45'}`}
                      style={active ? { backgroundColor: `${s.color}1a`, color: s.color } : {}}>
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <StackedBarChart data={analytics.statusSeries} series={activeStatusSeries} scaleMax={statusScaleMax} height={180} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="google-card p-6 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
              <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-5 flex items-center gap-1.5">
                <CalendarDays className="w-4 h-4 text-[#1a73e8]" /> Bookings by Day of Week
              </h3>
              <BarChartComp data={byDayOfWeek} color="#1a73e8" height={150} />
            </div>

            <div className="google-card p-6 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
              <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-5">New vs Returning Bookings</h3>
              <div className="flex items-center gap-6">
                <DonutChart segments={[{ value: analytics.newBookings, color: '#34A853' }, { value: analytics.returningBookings, color: '#9334e6' }]} />
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#34A853]" />
                    <span className="text-[#5f6368] dark:text-[#9aa0a6]">New:</span>
                    <span className="font-bold text-[#202124] dark:text-[#e8eaed]">{analytics.newBookings}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#9334e6]" />
                    <span className="text-[#5f6368] dark:text-[#9aa0a6]">Returning:</span>
                    <span className="font-bold text-[#202124] dark:text-[#e8eaed]">{analytics.returningBookings}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {section === 'revenue' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={IndianRupee} label="Total Revenue" value={`₹${realizedRevenue.toLocaleString('en-IN')}`} tone="green" />
            <StatCard icon={Wallet} label="Avg Booking Value" value={`₹${avgBookingValue}`} tone="blue" />
            <StatCard icon={Receipt} label="Revenue Today" value={revenueTodayEntry ? `₹${revenueTodayEntry.revenue.toLocaleString('en-IN')}` : '—'} sublabel={!revenueTodayEntry ? 'switch to 30/90 days' : undefined} tone="amber" />
            <StatCard icon={TrendingUp} label="Projected Monthly" value={`₹${projectedMonthly.toLocaleString('en-IN')}`} sublabel="based on recent daily avg" tone="purple" />
          </div>

          <div className="google-card p-5 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] flex items-center gap-1.5">
                <PiggyBank className="w-4 h-4 text-[#1a73e8]" /> This Month&apos;s Goal
              </h3>
              <button onClick={() => navigate('/')} className="text-[11px] text-[#1a73e8] dark:text-[#8ab4f8] hover:underline font-medium cursor-pointer">
                Edit on Dashboard
              </button>
            </div>
            {analytics.revenueGoal > 0 ? (
              <>
                <div className="flex items-end justify-between">
                  <span className="text-xl font-bold text-[#202124] dark:text-[#e8eaed]">₹{thisMonthRevenue.toLocaleString('en-IN')}</span>
                  <span className="text-xs text-[#9aa0a6] mb-0.5">of ₹{analytics.revenueGoal.toLocaleString('en-IN')}</span>
                </div>
                <div className="h-2 rounded-full bg-[#f1f3f4] dark:bg-[#202124] mt-2.5 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#1a73e8] to-[#34A853] transition-all" style={{ width: `${goalPct}%` }} />
                </div>
                <p className="text-[11px] text-[#9aa0a6] mt-2">
                  {goalPct >= 100 ? 'Goal reached this month!' : `${goalPct}% of this month's target — ₹${(analytics.revenueGoal - thisMonthRevenue).toLocaleString('en-IN')} to go.`}
                </p>
              </>
            ) : (
              <p className="text-xs text-[#9aa0a6] py-2">No monthly goal set yet — set one from the Dashboard.</p>
            )}
          </div>

          <div className="google-card p-6 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
            <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-6">Revenue Trend</h3>
            {revenueTrend.length === 0 ? <p className="text-xs text-[#9aa0a6] text-center py-10">No completed bookings in this range yet.</p> : <LineChart data={revenueTrend} color="#34A853" />}
          </div>

          <div className="google-card p-6 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
            <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-1">Revenue by Pass Type</h3>
            <p className="text-[11px] text-[#9aa0a6] mb-4">Every booking today is a single day's slot — this fills in once weekly/monthly passes can be purchased.</p>
            {planMixSegments.length === 0 ? (
              <p className="text-xs text-[#9aa0a6] text-center py-6">No completed bookings in this range yet.</p>
            ) : (
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <DonutChart segments={planMixSegments} />
                <div className="space-y-2 text-sm flex-1 w-full">
                  {planMixSegments.map((p) => (
                    <div key={p.label} className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
                        <span className="text-[#5f6368] dark:text-[#9aa0a6]">{p.label}</span>
                      </span>
                      <span className="font-bold text-[#202124] dark:text-[#e8eaed]">₹{p.value.toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {section === 'occupancy' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Percent} label="Avg Weekly Occupancy" value={`${avgWeeklyOccupancy}%`} tone="blue" />
            <StatCard icon={LayoutGrid} label="Time Bands Tracked" value={timeBandsTracked} sublabel="of 28 possible" tone="purple" />
            <StatCard icon={Flame} label="Busiest Time Band" value={busiestBand.label} sublabel={busiestBand.pctAvg >= 0 ? `${Math.round(busiestBand.pctAvg)}% booked` : undefined} tone="red" />
            <StatCard icon={Target} label="Closed Days / Week" value={closedDaysCount} tone="amber" />
          </div>

          <div className="google-card p-6 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043] overflow-x-auto">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
              <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed]">Weekly Occupancy Heatmap</h3>
              <div className="flex items-center gap-2 text-[10px] text-[#9aa0a6]">
                <span>Quiet</span>
                <div className="w-24 h-2.5 rounded-full" style={{ background: 'linear-gradient(90deg, #34A853, #FBBC05, #EA4335)' }} />
                <span>Busy</span>
              </div>
            </div>
            <div className="w-fit">
              <div className="grid grid-cols-[64px_repeat(7,44px)] gap-1 mb-1">
                <div />
                {heatmapByDay.map((d) => (
                  <div key={d.label} className="text-center text-[10px] font-semibold text-[#5f6368] dark:text-[#9aa0a6]">{d.label}</div>
                ))}
              </div>
              {BAND_ORDER.map((bandKey, bandIdx) => (
                <div key={bandKey} className="grid grid-cols-[64px_repeat(7,44px)] gap-1 mb-1">
                  <div className="text-[10px] text-[#5f6368] dark:text-[#9aa0a6] flex flex-col justify-center pr-1">
                    <span className="font-semibold leading-tight capitalize">{bandKey}</span>
                    <span className="text-[8px] text-[#9aa0a6] leading-tight">{TIME_BAND_META[bandKey].sub}</span>
                  </div>
                  {heatmapByDay.map((d) => {
                    const cell = d.bands[bandIdx];
                    const empty = cell.pct === null;
                    return (
                      <div key={d.label} title={empty ? 'No slots' : `${Math.round(cell.pct)}% booked`}
                        className={`aspect-square rounded-md flex items-center justify-center text-[9px] font-semibold text-[#202124] dark:text-[#e8eaed] ${empty ? 'border border-dashed border-[#dadce0] dark:border-[#3c4043]' : ''}`}
                        style={{ backgroundColor: empty ? 'transparent' : occupancyColor(cell.pct / 100) }}>
                        {empty ? '–' : Math.round(cell.pct)}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="google-card p-6 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
              <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-5">Occupancy by Time Band</h3>
              <BarChartComp data={bandAverages} orientation="horizontal" colorFn={(d) => occupancySolid(d.value / 100)} valueFormatter={(v) => `${v}%`} />
            </div>

            <div className="google-card p-6 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043] flex flex-col items-center justify-center">
              <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-4 self-start flex items-center gap-1.5">
                <Gauge className="w-4 h-4 text-[#1a73e8]" /> Weekly Occupancy
              </h3>
              <GaugeChart pct={avgWeeklyOccupancy / 100} color={occupancySolid(avgWeeklyOccupancy / 100)} centerLabel={`${avgWeeklyOccupancy}%`} centerSubLabel="Occupied" />
              <p className="text-[11px] text-[#9aa0a6] text-center mt-3 max-w-[220px]">Average booked capacity across all open time bands in this range.</p>
            </div>
          </div>
        </div>
      )}

      {section === 'customers' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Users} label="Total Customers" value={analytics.totalCustomers} tone="blue" />
            <StatCard icon={Repeat} label="Repeat Customers" value={analytics.repeatCustomers} tone="purple" />
            <StatCard icon={Target} label="Avg Bookings / Customer" value={analytics.avgBookingsPerCustomer} tone="green" />
            <StatCard icon={Wallet} label="Avg Spend / Customer" value={`₹${analytics.avgSpendPerCustomer}`} tone="amber" />
          </div>

          <div className="google-card p-6 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
            <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-5 flex items-center gap-1.5">
              <UserPlus className="w-4 h-4 text-[#1a73e8]" /> New vs Returning Customers
            </h3>
            {analytics.customerSeries.length === 0 ? (
              <p className="text-xs text-[#9aa0a6] text-center py-6">No customer activity in this range yet.</p>
            ) : (
              <>
                <StackedBarChart data={analytics.customerSeries} series={CUSTOMER_SEGMENT_SERIES} scaleMax={customerScaleMax} height={180} />
                <div className="flex items-center gap-4 mt-4 pt-4 border-t border-[#e8eaed] dark:border-[#3c4043]">
                  {CUSTOMER_SEGMENT_SERIES.map((s) => (
                    <span key={s.key} className="flex items-center gap-1.5 text-[11px] text-[#5f6368] dark:text-[#9aa0a6]">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                      {s.label}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="google-card p-6 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
            <div className="flex items-center gap-2 pb-4 border-b border-[#e8eaed] dark:border-[#3c4043] mb-4">
              <Trophy className="w-5 h-5 text-[#f29900]" />
              <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed]">Top Customers by Spend</h3>
            </div>
            {analytics.topCustomers.length === 0 ? (
              <p className="text-xs text-[#9aa0a6] text-center py-6">No customer data yet.</p>
            ) : (
              <div className="space-y-6">
                <BarChartComp data={analytics.topCustomers.map((c) => ({ label: c.name, value: c.spend }))} orientation="horizontal" color="#f29900" valueFormatter={(v) => `₹${v.toLocaleString('en-IN')}`} />
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="text-[11px] font-semibold text-[#5f6368] dark:text-[#9aa0a6] uppercase tracking-wider border-b border-[#e8eaed] dark:border-[#3c4043]">
                        <th className="py-2 pr-4">Customer</th>
                        <th className="py-2 pr-4">Bookings</th>
                        <th className="py-2 pr-4">Total Spend</th>
                        <th className="py-2">Last Visit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#f1f3f4] dark:divide-[#3c4043]">
                      {analytics.topCustomers.map((c, i) => (
                        <tr key={c.email}>
                          <td className="py-3 pr-4">
                            <div className="flex items-center gap-2.5">
                              <span className="w-6 h-6 rounded-full bg-[#e8f0fe] dark:bg-[#1a2e4c] text-[#1a73e8] dark:text-[#8ab4f8] flex items-center justify-center text-[10px] font-bold shrink-0">{i + 1}</span>
                              <div>
                                <div className="font-semibold text-[#202124] dark:text-[#e8eaed]">{c.name}</div>
                                <div className="text-[11px] text-[#9aa0a6]">{c.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 pr-4 text-[#3c4043] dark:text-[#dadce0]">{c.count}</td>
                          <td className="py-3 pr-4 font-bold text-[#202124] dark:text-[#e8eaed]">₹{c.spend.toLocaleString('en-IN')}</td>
                          <td className="py-3 text-[#5f6368] dark:text-[#9aa0a6]">{c.lastVisit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
